/**
 * Authenticated layout shell — sidebar layout (SimpliWork design spec).
 *
 * Left sidebar (224px, gray-950):
 *   - Brand mark + "Relay" wordmark + "by SimpliWork" tag
 *   - Dashboard shortcut + module nav (Agents / Leads / Content) with entitlement gate
 *   - Settings expandable group (Billing, Integrations, Secrets, Twilio, Booking, Team, Audit)
 *   - User row (avatar→opens Clerk profile modal, name, role, sign-out)
 *
 * Top bar: auto-breadcrumb from pathname + notification bell + search + help.
 * Content: <Outlet /> in a scrollable flex-1 panel.
 */

import { useClerk, useUser } from "@clerk/clerk-react";
import {
  Bell,
  Bot,
  ChevronRight,
  HelpCircle,
  LayoutDashboard,
  ListChecks,
  Lock,
  LogOut,
  type LucideIcon,
  PenLine,
  Receipt,
  Search,
  Settings2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { ByokTeardownBanner } from "@/components/byok-teardown-banner";
import { Toaster } from "@/components/ui/toast";
import { useMe } from "@/hooks/use-me";
import { hasModuleAccess, MODULES, type ModuleId } from "@/lib/modules";
import { cn } from "@/lib/utils";

const buildVersion = import.meta.env.VITE_BUILD_VERSION ?? "dev";

// ── Constants ────────────────────────────────────────────────────────────────

const SETTINGS_ITEMS = [
  { to: "/settings/billing", label: "Billing", testId: "nav-billing" },
  { to: "/connections", label: "Connections", testId: "nav-connections" },
  { to: "/settings/secrets", label: "Secrets", testId: "nav-secrets" },
  { to: "/settings/team", label: "Team", testId: "nav-team" },
  { to: "/settings/audit", label: "Audit", testId: "nav-audit" },
  { to: "/automations", label: "Automations", testId: "nav-automations" },
] as const;

const MODULE_ICONS: Record<ModuleId, LucideIcon> = {
  agents: Bot,
  leads: Users,
  answer: ListChecks,
  content: PenLine,
  quote: Receipt,
};

// Human-readable labels for URL path segments used in breadcrumb generation.
const SEGMENT_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  agents: "Agents",
  leads: "Leads",
  content: "Content",
  quote: "Quote",
  answer: "Answer",
  questionnaires: "Questionnaires",
  library: "Library",
  catalog: "Catalog",
  playbooks: "Playbooks",
  documents: "Documents",
  drafts: "Drafts",
  sequences: "Sequences",
  connections: "Connections",
  runs: "Runs",
  analytics: "Analytics",
  settings: "Settings",
  billing: "Billing",
  secrets: "Secrets",
  twilio: "Connections",
  booking: "Connections",
  team: "Team",
  audit: "Audit",
  automations: "Automations",
  new: "New",
  configure: "Configure",
  history: "History",
  inbox: "Inbox",
  funnel: "Funnel",
  posts: "Posts",
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function buildBreadcrumbs(pathname: string): string[] {
  return pathname
    .split("/")
    .filter(Boolean)
    .filter((seg) => !UUID_RE.test(seg))
    .map((seg) => SEGMENT_LABELS[seg] ?? seg)
    .slice(0, 3);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NavItem({
  to,
  label,
  icon: Icon,
  testId,
  currentPath,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  testId: string;
  currentPath: string;
}) {
  const active = currentPath === to || currentPath.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      data-testid={testId}
      className={cn(
        "flex items-center gap-2.5 rounded-lg px-3.5 py-2 text-[0.84rem] font-medium transition-all",
        active
          ? "bg-blue-600/[0.18] text-white"
          : "text-white/40 hover:bg-white/5 hover:text-white/85",
      )}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-blue-500" : "text-white/25")} />
      {label}
    </Link>
  );
}

function SettingsSubItem({
  to,
  label,
  currentPath,
  testId,
}: {
  to: string;
  label: string;
  currentPath: string;
  testId: string;
}) {
  const active = currentPath === to || currentPath.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      data-testid={testId}
      className={cn(
        "flex items-center gap-2 rounded-md py-1.5 pl-[2.625rem] pr-3.5 text-[0.78rem] font-medium transition-all",
        active ? "text-blue-400" : "text-white/30 hover:bg-white/[0.04] hover:text-white/70",
      )}
    >
      <span className="h-1 w-1 shrink-0 rounded-full bg-current opacity-60" />
      {label}
    </Link>
  );
}

// ── AppShell ─────────────────────────────────────────────────────────────────

export function AppShell() {
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const me = useMe();
  const location = useLocation();

  const isOnSettingsPath = SETTINGS_ITEMS.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
  );
  const [settingsOpen, setSettingsOpen] = useState(isOnSettingsPath);

  // Auto-expand settings accordion when navigating to a settings route.
  useEffect(() => {
    if (isOnSettingsPath) setSettingsOpen(true);
  }, [isOnSettingsPath]);

  const breadcrumbs = buildBreadcrumbs(location.pathname);

  const userName = user?.firstName ?? user?.emailAddresses[0]?.emailAddress ?? "";
  const userInitials = userName.slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-gray-900 antialiased">
      {/* ── Sidebar ──────────────────────────────────────── */}
      <aside
        className="flex w-56 min-w-56 flex-col overflow-hidden border-r border-white/[0.07] bg-gray-950"
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-4 py-5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-[0_0_16px_rgba(37,99,235,0.4)]">
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <Link to="/dashboard" data-testid="brand-link" className="flex flex-col">
            <span className="text-[1.05rem] font-extrabold leading-none tracking-tight text-white">
              Relay
            </span>
            <span className="mt-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-blue-500">
              by SimpliWork
            </span>
          </Link>
        </div>

        {/* Nav */}
        <nav
          className="flex-1 overflow-y-auto pb-2 [&::-webkit-scrollbar]:hidden"
          aria-label="Main"
        >
          {/* Dashboard */}
          <div className="px-2 pt-1">
            <NavItem
              to="/dashboard"
              label="Dashboard"
              icon={LayoutDashboard}
              testId="nav-dashboard"
              currentPath={location.pathname}
            />
          </div>

          {/* Modules */}
          <p className="px-4 pb-1.5 pt-4 text-[0.62rem] font-bold uppercase tracking-[0.2em] text-white/20">
            Modules
          </p>
          <div className="px-2">
            {MODULES.map((mod) => {
              const Icon = MODULE_ICONS[mod.id];
              const entitled = hasModuleAccess(user?.publicMetadata, mod.id);
              if (!entitled) {
                return (
                  <div
                    key={mod.id}
                    data-testid={`nav-module-${mod.id}`}
                    title={`${mod.label} — not included in your plan`}
                    className="flex cursor-not-allowed items-center gap-2.5 rounded-lg px-3.5 py-2 text-[0.84rem] font-medium text-white/20"
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {mod.label}
                    <Lock className="ml-auto h-3 w-3" />
                  </div>
                );
              }
              return (
                <NavItem
                  key={mod.id}
                  to={mod.basePath}
                  label={mod.label}
                  icon={Icon}
                  testId={`nav-module-${mod.id}`}
                  currentPath={location.pathname}
                />
              );
            })}
          </div>

          {/* Settings */}
          <p className="px-4 pb-1.5 pt-4 text-[0.62rem] font-bold uppercase tracking-[0.2em] text-white/20">
            Settings
          </p>
          <div className="px-2">
            <button
              type="button"
              data-testid="nav-settings"
              onClick={() => setSettingsOpen((o) => !o)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3.5 py-2 text-[0.84rem] font-medium transition-all",
                isOnSettingsPath
                  ? "bg-blue-600/[0.18] text-white"
                  : "text-white/40 hover:bg-white/5 hover:text-white/85",
              )}
            >
              <Settings2
                className={cn(
                  "h-4 w-4 shrink-0",
                  isOnSettingsPath ? "text-blue-500" : "text-white/25",
                )}
              />
              Settings
              <ChevronRight
                className={cn(
                  "ml-auto h-3.5 w-3.5 text-white/20 transition-transform duration-200",
                  settingsOpen && "rotate-90",
                )}
              />
            </button>

            <div
              className="overflow-hidden transition-all duration-200"
              style={{ maxHeight: settingsOpen ? "400px" : "0" }}
            >
              {SETTINGS_ITEMS.map((item) => (
                <SettingsSubItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  currentPath={location.pathname}
                  testId={item.testId}
                />
              ))}
            </div>
          </div>
        </nav>

        {/* User row */}
        <div className="border-t border-white/[0.07] px-2 py-2.5">
          <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2">
            <button
              type="button"
              data-testid="header-user-profile-btn"
              title="Edit profile"
              onClick={() => openUserProfile()}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg transition-colors hover:bg-white/5"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-[0.65rem] font-bold text-white">
                {userInitials}
              </div>
              <div className="flex min-w-0 flex-1 flex-col text-left">
                <span
                  className="truncate text-[0.78rem] font-semibold text-white/80"
                  data-testid="header-user"
                >
                  {userName}
                </span>
                <span className="text-[0.65rem] text-white/28">{me.data?.role ?? "member"}</span>
              </div>
            </button>
            <Link
              to="/sign-out"
              data-testid="header-sign-out"
              title="Sign out"
              className="rounded p-1 text-white/20 transition-colors hover:bg-white/5 hover:text-white/60"
            >
              <LogOut className="h-3.5 w-3.5" />
            </Link>
          </div>
          {buildVersion !== "dev" && (
            <p className="mt-1 px-2.5 text-[0.6rem] text-white/15">build {buildVersion}</p>
          )}
        </div>
      </aside>

      {/* ── Main area ─────────────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 bg-white/96 px-7 shadow-[0_2px_16px_rgba(0,0,0,0.04)] backdrop-blur-md">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span className="text-[0.7rem] text-gray-300" aria-hidden="true">
                    ›
                  </span>
                )}
                <span
                  className={cn(
                    "text-[0.8rem]",
                    i === breadcrumbs.length - 1 ? "font-semibold text-gray-900" : "text-gray-400",
                  )}
                >
                  {crumb}
                </span>
              </span>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              title="Notifications"
            >
              <Bell className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              title="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              title="Help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </header>

        <ByokTeardownBanner />

        <main className="flex-1 overflow-y-auto p-7">
          <Outlet />
        </main>
      </div>

      <Toaster />
    </div>
  );
}

export default AppShell;
